import React, { useState, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import CustomTypography from "../../globalStyles/fonts";
import AutocompleteWithPagination from "../autocomplete-with-pagination";
import { productService } from "../../utils/services/productService";
import { inventoryService } from "../../utils/services/inventory.service";
import { inventoryConfigService } from "../../utils/services/inventory-config.service";
import { isMobile } from "../../globalStyles/mobile";
import MultiKeyAutocompleteWithPagination from "../multi-key-autocomplete-with-pagination";
import { useDarkMode } from "../context/darkModeContext";

const chartHeight = 260; // px, the visible height of the chart area
const maxY = 10000; // or make this dynamic
const getYAxisTicks = (max, steps = 5) => {
  const interval = max / steps;
  return Array.from({ length: steps + 1 }, (_, i) =>
    Math.round(max - i * interval),
  );
};

const Inventory = ({ clientId }) => {
  const { colors } = useDarkMode();

  const [selectedSKU, setSelectedSKU] = useState(null);
  const [hoveredBar, setHoveredBar] = useState(null);
  const [animatedValue, setAnimatedValue] = useState(0);
  const animationRef = useRef();
  const [data, setData] = useState([]);
  const [barHeights, setBarHeights] = useState([]);
  const [safeInventoryCount, setSafeInventoryCount] = useState(
    getYAxisTicks(maxY), // Initialize with maxY instead of 1000
  );

  const fetchInventory = async (skuObj) => {
    if (!skuObj) {
      setData([]);
      setBarHeights([]);

      setSafeInventoryCount(getYAxisTicks(maxY));
      return;
    }
    try {
      let inventory = await inventoryService.get(
        skuObj._id,
        skuObj.variants.sku,
      );
      // Aggregate data from all providers into a single combined object
      const aggregatedData = (inventory.data || []).reduce(
        (acc, item) => {
          acc.in_hand += item.in_hand || 0;
          acc.quantity += item.quantity || 0;
          acc.inbound_to_warehouse += item.inbound_to_warehouse || 0;
          return acc;
        },
        { in_hand: 0, quantity: 0, inbound_to_warehouse: 0 },
      );

      // Calculate dynamic maxY based on the highest value among the 3 bars
      const maxValue = Math.max(
        aggregatedData.in_hand,
        aggregatedData.quantity,
        aggregatedData.inbound_to_warehouse,
      );
      const targetMaxY = maxValue * 2; // 2 times the max value

      // Round to nice round numbers (nearest 100)
      const dynamicMaxY = Math.ceil(targetMaxY / 100) * 100;

      // Create single chart data object with combined values
      const chartData = [
        {
          name: "Combined",
          values: [
            aggregatedData.in_hand,
            aggregatedData.quantity,
            aggregatedData.inbound_to_warehouse,
          ],
          maxY: dynamicMaxY, // Store the dynamic maxY with the data
        },
      ];
      setData(chartData);
      setBarHeights(Array(3).fill(0)); // Only 3 bars total (combined)
      // Fetch inventory safe count which will handle Y-axis scaling
      fetchInventorySafeCount(
        skuObj,
        inventory.data.reduce((acc, item) => {
          return acc + (item.quantity || 0);
        }, 0),
        dynamicMaxY, // Pass the dynamic maxY to the function
      );
    } catch (e) {
      setData([]);
      setBarHeights([]);
      setSafeInventoryCount(getYAxisTicks(maxY)); // Set to maxY on error too
      console.error(e);
    }
  };

  // Fetch inventory safe count which handles Y-axis scaling based on safety stock configuration
  const fetchInventorySafeCount = async (skuObj, qty, dynamicMaxY) => {
    try {
      let inventory_config = await inventoryConfigService.fetch(
        100,
        0,
        clientId,
        "",
        skuObj._id,
      );
      if (inventory_config.data && inventory_config.data.length > 0) {
        const safetyStockTotal = inventory_config.data.reduce((acc, item) => {
          return acc + (item.safety_stock_count || 0);
        }, 0);

        // Get the maximum value from the three inventory values
        const maxInventoryValue = Math.max(...data[0].values);

        // If safety stock data is present, compare with max inventory value
        if (safetyStockTotal > 0) {
          // If safety stock is less than max inventory value, use max inventory value
          // Otherwise use safety stock as the maximum
          const finalMaxValue =
            safetyStockTotal < maxInventoryValue
              ? maxInventoryValue
              : safetyStockTotal;
          setSafeInventoryCount(
            getYAxisTicks(Math.max(finalMaxValue, dynamicMaxY)),
          );
        } else {
          setSafeInventoryCount(getYAxisTicks(dynamicMaxY));
        }
      } else {
        // No config data found, use dynamicMaxY
        setSafeInventoryCount(getYAxisTicks(dynamicMaxY));
      }
    } catch (e) {
      console.error(e);
      // On error, fallback to dynamicMaxY
      setSafeInventoryCount(getYAxisTicks(dynamicMaxY));
      return;
    }
  };

  // Animate all bars on mount or when data changes
  useEffect(() => {
    if (!data.length) return;
    let raf;
    const duration = 500;
    const startTime = performance.now();
    // Get the values from the combined data (only one provider now)
    const currentMaxY = data[0].maxY || maxY; // Use dynamic maxY if available
    const ends = data[0].values.map((val) => (val / currentMaxY) * chartHeight);
    const animate = (now) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      setBarHeights(ends.map((end) => progress * end));
      if (progress < 1) {
        raf = requestAnimationFrame(animate);
      }
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [data]);

  const handleSKUChange = (sku) => {
    setSelectedSKU(sku);
    if (sku) {
      fetchInventory(sku);
    } else {
      setData([]);
      setBarHeights([]);
      setSafeInventoryCount(getYAxisTicks(maxY)); // Set to maxY when no SKU selected
    }
  };

  // Animate value on hover
  useEffect(() => {
    if (hoveredBar !== null && data.length) {
      let start = 0;
      // Get the value for the hovered bar (direct index since we only have one provider)
      const end = data[0]?.values[hoveredBar] || 0;
      const duration = 400; // ms
      const startTime = performance.now();
      const animate = (now) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        setAnimatedValue(Math.round(progress * end));
        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate);
        }
      };
      animationRef.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationRef.current);
    } else {
      setAnimatedValue(0);
    }
  }, [hoveredBar, data]);

  // Bar labels
  const barLabels = ["On Hand", "Available", "Inbound to Warehouse"];

  return (
    <Box
      sx={{
        paddingTop: 1,
        background: colors.bgPaper,
        borderRadius: 3,
        border: `1px solid ${colors.border}`,
        boxShadow: "none",
        minHeight: 418,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
          borderBottom: `1px solid ${colors.borderLight}`,
          paddingLeft: 2,
          paddingRight: 2,
        }}
      >
        <CustomTypography
          variant="subtitle1"
          fontWeight={700}
          letterSpacing={1}
          sx={{ fontSize: isMobile() ? 12 : 14 }}
        >
          INVENTORY
        </CustomTypography>
        <MultiKeyAutocompleteWithPagination
          selectedClient={{ _id: clientId }}
          backgroundColor={colors.bgPaper}
          customLabel={"SKU"}
          selectedItem={selectedSKU || null}
          onDataChange={(sku) => {
            if (sku) {
              handleSKUChange(sku);
            } else {
              handleSKUChange("");
            }
          }}
          width={"200px"}
          primaryKey={"variant.sku"}
          parenthesisKey={"variant.title"}
          secondLineKey={"name"}
          apiEndPoint={(limit, skip, search, client) =>
            productService.fetchSKU(client, search, limit, skip)
          }
          initSet
        />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          paddingLeft: 1,
        }}
      >
        {/* HTML/CSS Bar Chart */}
        <div
          style={{
            width: "100%",
            height: chartHeight,
            display: "flex",
            alignItems: "flex-end",
            justifyContent: data.length ? "space-around" : "center", // changed from flex-start
            position: "relative",
            paddingLeft: 40,
            paddingRight: 20,
            boxSizing: "border-box",
          }}
        >
          {/* Dotted Y-axis line from the start */}
          <div
            style={{
              position: "absolute",
              left: 32,
              top: 0,
              bottom: 0,
              width: 0,
              borderLeft: `1.5px dashed ${colors.dottedLine}`,
              zIndex: 1,
            }}
          />
          {/* Y-axis grid lines */}
          {safeInventoryCount.map((tick, idx) => {
            const currentMaxY = data.length > 0 ? data[0].maxY || maxY : maxY;
            return (
              <div
                key={tick}
                style={{
                  position: "absolute",
                  left: 32,
                  right: 0,
                  bottom: `${(tick / currentMaxY) * chartHeight}px`,
                  borderBottom:
                    idx === safeInventoryCount.length - 1
                      ? "1.5px solid #aaa"
                      : `1px dashed ${colors.gridLine}`,
                  zIndex: 0,
                }}
              />
            );
          })}
          {/* Bars: render 3 bars for combined data (On Hand, Available, Inbound) */}
          {data.length === 0 && (
            <span style={{ color: colors.textTertiary, fontSize: 14 }}>
              Select SKU to view inventory
            </span>
          )}
          {data.length > 0 &&
            data[0].values.map((val, valueIdx) => {
              const barHeightPx = barHeights[valueIdx] || 0;
              // Use different colors for each bar type
              let color = "#3BBDC4"; // Default for On Hand
              return (
                <div
                  key={valueIdx}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    minWidth: 0,
                    position: "relative",
                    height: chartHeight,
                    justifyContent: "flex-end",
                    marginLeft: valueIdx > 0 ? 24 : 0, // space between bars
                  }}
                >
                  {/* Max bar (background) */}
                  <div
                    style={{
                      height: "100%",
                      width: 36,
                      background: colors.hover,
                      borderRadius: "18px 18px 0 0",
                      position: "relative",
                      display: "flex",
                      alignItems: "flex-end",
                      justifyContent: "center",
                    }}
                  >
                    {/* Value bar (foreground) */}
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: `${barHeightPx}px`,
                        background: color,
                        boxShadow: "0px -3px 6px 0px rgba(0,0,0,0.04)",
                        transition: "height 0.3s",
                        zIndex: 1,
                        overflow: "visible",
                        display: "flex",
                        justifyContent: "center",
                        cursor: "pointer",
                      }}
                      onMouseEnter={() => setHoveredBar(valueIdx)}
                      onMouseLeave={() => setHoveredBar(null)}
                    >
                      {/* Top cap */}
                      <div
                        style={{
                          position: "absolute",
                          top: -3,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 48,
                          height: 4,
                          background: color,
                          borderRadius: "8px 8px 8px 8px",
                          boxShadow:
                            "0 8px 18px 0 rgba(0,0,0,0.32), 8px 0 16px 0 rgba(0,0,0,0.18), -8px 0 16px 0 rgba(0,0,0,0.18), 0 2px 8px 0 rgba(0,0,0,0.18)",
                          zIndex: 2,
                        }}
                      />
                      {/* Value tooltip on hover with animation */}
                      {hoveredBar === valueIdx && (
                        <div
                          style={{
                            position: "absolute",
                            top: -32,
                            left: "50%",
                            transform: "translateX(-50%)",
                            background: "#222",
                            color: "#fff",
                            padding: "2px 8px",
                            borderRadius: 6,
                            fontSize: 12,
                            whiteSpace: "nowrap",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
                            zIndex: 10,
                            transition: "opacity 0.2s",
                          }}
                        >
                          {animatedValue}
                        </div>
                      )}
                    </div>
                    {/* Top shadow line */}
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: `${barHeightPx}px`,
                        height: 2,
                        background: "rgba(0,0,0,0.18)",
                        borderRadius: "2px",
                        filter: "blur(15px)",
                        boxShadow: "0 10px 12px 0 rgba(0,0,0,0.25)",
                        zIndex: 2,
                        display: barHeightPx > 0 ? "block" : "none",
                      }}
                    />
                  </div>
                </div>
              );
            })}
          {/* Y-axis labels */}
          {safeInventoryCount.map((tick) => {
            const currentMaxY = data.length > 0 ? data[0].maxY || maxY : maxY;
            return (
              <span
                key={tick}
                style={{
                  position: "absolute",
                  left: 0,
                  bottom: `${(tick / currentMaxY) * chartHeight}px`,
                  fontSize: 10,
                  color: colors.textSecondary,
                  transform: "translateY(50%)",
                  zIndex: 2,
                  width: 32,
                  textAlign: "right",
                  paddingRight: 6,
                  pointerEvents: "none",
                }}
              >
                {tick}
              </span>
            );
          })}
        </div>
        {/* Bar labels below the graph, outside the chart */}
        {data.length > 0 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-around", // align right
              alignItems: "center",
              width: "93%",
              mt: 1,
              mb: 1,
              ml: "auto", // push to right
            }}
          >
            {barLabels.map((label, idx) => (
              <span
                key={label}
                style={{
                  fontSize: 12,
                  color: colors.textPrimary,
                  textAlign: "center",
                  minWidth: 80,
                  fontWeight: 600,
                }}
              >
                {label}
              </span>
            ))}
          </Box>
        )}
        {/* <CustomTypography
          variant="subtitle2"
          align="center"
          fontWeight={700}
          sx={{ mt: 2, letterSpacing: 1, color: "#222", fontSize: 10 }}
        >
          Redefined Logistics
        </CustomTypography> */}
      </Box>
    </Box>
  );
};

export default Inventory;
